import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Visibility, 
  Delete, 
  Upload,
  CheckCircle,
  Schedule,
  Error,
  CloudUpload,
  Mic,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as meetingService from '../../services/meetingService';

const Dashboard = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, meeting: null });
  const [retryingMeetings, setRetryingMeetings] = useState(new Set());
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Dashboard - EchoNote';
    loadMeetings();
    
    // Auto-refresh every 10 seconds to update processing status
    // Only continue refreshing if user is still logged in
    const interval = setInterval(() => {
      if (user) {
        loadMeetings();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [user]);

  const loadMeetings = async () => {
    try {
      const response = await meetingService.getMeetings();
      setMeetings(response.data);
    } catch (error) {
      setError('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await meetingService.deleteMeeting(deleteDialog.meeting.id);
      setMeetings(meetings.filter(m => m.id !== deleteDialog.meeting.id));
      setDeleteDialog({ open: false, meeting: null });
    } catch (error) {
      setError('Failed to delete meeting');
    }
  };

  const handleRetry = async (meetingId) => {
    setRetryingMeetings(prev => new Set(prev).add(meetingId));
    setError('');
    try {
      const response = await meetingService.reprocessMeeting(meetingId);
      // Update the meeting in the list with the new status
      setMeetings(meetings.map(m => 
        m.id === meetingId ? response.data : m
      ));
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to retry meeting transcription';
      setError(errorMessage);
    } finally {
      setRetryingMeetings(prev => {
        const newSet = new Set(prev);
        newSet.delete(meetingId);
        return newSet;
      });
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      uploaded: { color: 'default', icon: <CloudUpload />, label: 'Uploaded' },
      processing: { color: 'warning', icon: <Schedule />, label: 'Processing' },
      completed: { color: 'success', icon: <CheckCircle />, label: 'Completed' },
      failed: { color: 'error', icon: <Error />, label: 'Failed' }
    };

    const config = statusConfig[status] || statusConfig.uploaded;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      width: 200,
      flex: 1
    },
    {
      field: 'uploadedBy',
      headerName: 'Uploaded By',
      width: 150,
      renderCell: (params) => 
        `${params.row.uploadedBy.firstName} ${params.row.uploadedBy.lastName}`
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'reviewCount',
      headerName: 'Needs Review',
      width: 130,
      renderCell: (params) =>
        params.value ? (
          <Chip label={`${params.value} flagged`} size="small" color="warning" variant="outlined" />
        ) : (
          <Typography variant="body2" color="textSecondary">
            —
          </Typography>
        )
    },
    {
      field: 'createdAt',
      headerName: 'Upload Date',
      width: 150,
      renderCell: (params) => 
        new Date(params.value).toLocaleDateString()
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => navigate(`/meeting/${params.row.id}`)}
              color="primary"
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          {params.row.status === 'failed' && user?.can_reprocess_failed_meetings && (
            <Tooltip title="Retry">
              <IconButton
                size="small"
                onClick={() => handleRetry(params.row.id)}
                color="warning"
                disabled={retryingMeetings.has(params.row.id)}
              >
                {retryingMeetings.has(params.row.id) ? (
                  <CircularProgress size={20} color="warning" />
                ) : (
                  <Refresh />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => setDeleteDialog({ open: true, meeting: params.row })}
              color="error"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const stats = {
    total: meetings.length,
    processing: meetings.filter(m => m.status === 'processing').length,
    completed: meetings.filter(m => m.status === 'completed').length,
    failed: meetings.filter(m => m.status === 'failed').length
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => navigate('/upload')}
          >
            Upload Meeting
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Mic />}
            onClick={() => navigate('/record')}
          >
            Record Live
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Meetings
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Processing
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.processing}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Failed
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.failed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Meetings Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Meetings
          </Typography>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={meetings}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              disableSelectionOnClick
            />
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, meeting: null })}
      >
        <DialogTitle>Delete Meeting</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{deleteDialog.meeting?.title}"? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, meeting: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;